import React, { useState, useEffect } from 'react'
import { Pencil, Star, X, Package, Leaf, ClipboardList, Plus, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageSpinner } from '@/components/ui/Spinner'
import { useIngredients, useSaveIngredient, useSuppliers } from '@/hooks/useMenu'
import {
  useInventoryPerishable,
  useUpsertPerishableStock,
  useInventoryNonperishable,
  useUpsertNonperishableStock,
  useCreatePurchaseOrder,
} from '@/hooks/useInventory'
import { supabase } from '@/lib/supabase'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import { formatDate } from '@/lib/utils'
import type { Ingredient, Supplier, ItemType, MaterialCategory, InventoryPerishable, InventoryNonperishable, Role } from '@/types/app'

const UNITS = ['g', 'kg', 'ml', 'L', 'lb', 'oz', 'gal', 'qt', 'fl_oz', 'piece', 'dozen', 'case', 'sheet', 'roll', 'bottle']

const MATERIAL_CATEGORIES: { value: MaterialCategory; label: string }[] = [
  { value: 'packaging', label: 'Packaging' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'kitchen_supply', label: 'Kitchen Supply' },
  { value: 'other', label: 'Other' },
]

const STOCK_ROLES: Role[] = ['owner', 'gm', 'ops', 'chef']

type TabKey = 'all' | 'ingredient' | 'material' | 'stock'

interface AttachedSupplier {
  id: string
  ingredient_id: string
  supplier_id: string
  is_preferred: boolean
  unit_price: number | null
  notes: string | null
  supplier: Supplier | null
}

interface PanelState {
  id?: string
  name: string
  brand: string
  unit: string
  cost_per_unit: string
  is_perishable: boolean
  item_type: ItemType
  material_category: MaterialCategory | null
  attachedSuppliers: AttachedSupplier[]
  addSupplierId: string
}

type IngredientWithSuppliers = Ingredient & { ingredient_suppliers: AttachedSupplier[] }

function getEffectiveCost(ing: IngredientWithSuppliers): number | null {
  const preferred = ing.ingredient_suppliers.find(s => s.is_preferred)
  if (preferred?.unit_price != null) return preferred.unit_price
  const anyWithPrice = ing.ingredient_suppliers.find(s => s.unit_price != null)
  if (anyWithPrice?.unit_price != null) return anyWithPrice.unit_price
  return ing.cost_per_unit
}

function getPreferredSupplierName(ing: IngredientWithSuppliers): string {
  const preferred = ing.ingredient_suppliers.find(s => s.is_preferred)
  return preferred?.supplier?.name ?? (ing.ingredient_suppliers[0]?.supplier?.name ?? '—')
}

function stockStatus(stock: InventoryPerishable): 'ok' | 'low' | 'order' {
  if (stock.par_level == null) return 'ok'
  if (stock.reorder_point != null && stock.quantity < stock.reorder_point) return 'order'
  if (stock.quantity === 0) return 'order'
  if (stock.quantity < stock.par_level) return 'low'
  return 'ok'
}

function StatusBadge({ status }: { status: 'ok' | 'low' | 'order' }) {
  if (status === 'ok') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
        OK
      </span>
    )
  }
  if (status === 'low') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
        Low
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
      Order Now
    </span>
  )
}

interface UpdateCountFormState {
  quantity: string
  expiry_date: string
  location: string
  notes: string
}

function UpdateCountRow({
  stock,
  ingredientUnit,
  onCancel,
}: {
  stock: InventoryPerishable
  ingredientUnit: string
  onCancel: () => void
}) {
  const upsert = useUpsertPerishableStock()
  const [form, setForm] = useState<UpdateCountFormState>({
    quantity: stock.quantity.toString(),
    expiry_date: stock.expiry_date ?? '',
    location: stock.location ?? '',
    notes: stock.notes ?? '',
  })
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    const qty = parseFloat(form.quantity)
    if (isNaN(qty) || qty < 0) {
      setError('Quantity must be a valid number')
      return
    }
    setError(null)
    try {
      await upsert.mutateAsync({
        ingredient_id: stock.ingredient_id,
        quantity: qty,
        unit: ingredientUnit,
        par_level: stock.par_level,
        reorder_point: stock.reorder_point,
        expiry_date: form.expiry_date || null,
        location: form.location || null,
        notes: form.notes || null,
      })
      onCancel()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  return (
    <tr className="bg-amber-50 border-b border-amber-100">
      <td colSpan={8} className="px-4 py-3">
        <div className="flex flex-wrap gap-3 items-end">
          {error && <p className="w-full text-sm text-red-600">{error}</p>}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-600">Quantity ({ingredientUnit})</label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              className="h-8 w-28 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-600">Expiry date</label>
            <input
              type="date"
              value={form.expiry_date}
              onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
              className="h-8 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-600">Location</label>
            <input
              type="text"
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Walk-in fridge"
              className="h-8 w-36 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-600">Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="h-8 w-40 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-2 pb-0.5">
            <Button size="sm" onClick={handleSave} loading={upsert.isPending}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </td>
    </tr>
  )
}

interface QuickPOItem {
  ingredient_id: string
  ingredient_name: string
  unit: string
  quantity_ordered: string
  unit_price: string
  notes: string
}

function QuickPOPanel({
  initialIngredients,
  allIngredients,
  onClose,
}: {
  initialIngredients: IngredientWithSuppliers[]
  allIngredients: IngredientWithSuppliers[]
  onClose: () => void
}) {
  const { data: suppliers } = useSuppliers()
  const createPO = useCreatePurchaseOrder()

  const [supplierId, setSupplierId] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<QuickPOItem[]>(() =>
    initialIngredients.map(ing => ({
      ingredient_id: ing.id,
      ingredient_name: ing.name,
      unit: ing.unit,
      quantity_ordered: '',
      unit_price: '',
      notes: '',
    }))
  )
  const [addIngredientId, setAddIngredientId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const perishableIngredients = allIngredients.filter(i => i.is_perishable && i.item_type === 'ingredient')
  const usedIds = new Set(items.map(i => i.ingredient_id))
  const availableToAdd = perishableIngredients.filter(i => !usedIds.has(i.id))

  function handleAddItem() {
    if (!addIngredientId) return
    const ing = allIngredients.find(i => i.id === addIngredientId)
    if (!ing) return
    setItems(prev => [
      ...prev,
      {
        ingredient_id: ing.id,
        ingredient_name: ing.name,
        unit: ing.unit,
        quantity_ordered: '',
        unit_price: '',
        notes: '',
      },
    ])
    setAddIngredientId('')
  }

  function handleRemoveItem(ingredientId: string) {
    setItems(prev => prev.filter(i => i.ingredient_id !== ingredientId))
  }

  async function handleCreate() {
    const validItems = items.filter(i => {
      const qty = parseFloat(i.quantity_ordered)
      return !isNaN(qty) && qty > 0
    })

    if (validItems.length === 0) {
      setError('Add at least one item with a quantity')
      return
    }

    setError(null)
    try {
      await createPO.mutateAsync({
        supplier_id: supplierId || null,
        notes: notes || null,
        items: validItems.map(i => ({
          ingredient_id: i.ingredient_id,
          quantity_ordered: parseFloat(i.quantity_ordered),
          unit: i.unit,
          unit_price: i.unit_price ? parseFloat(i.unit_price) : null,
          notes: i.notes || null,
        })),
      })
      setSuccess(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create PO')
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-semibold text-stone-900">Create Purchase Order</h2>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-5">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <ClipboardList size={22} className="text-emerald-600" />
            </div>
            <p className="text-stone-800 font-semibold text-lg">Draft PO created</p>
            <p className="text-stone-500 text-sm text-center">The purchase order has been saved as a draft.</p>
            <Button onClick={onClose} className="mt-2">Done</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-stone-700">Supplier</label>
                <select
                  value={supplierId}
                  onChange={e => setSupplierId(e.target.value)}
                  className="h-9 w-full rounded border border-stone-300 bg-white px-3 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">No supplier selected</option>
                  {(suppliers ?? []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-stone-700">Items</p>

                {items.length === 0 && (
                  <p className="text-xs text-stone-400">No items added yet.</p>
                )}

                {items.map(item => (
                  <div key={item.ingredient_id} className="p-3 rounded border border-stone-200 bg-stone-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-stone-800">{item.ingredient_name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.ingredient_id)}
                        className="text-stone-300 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1 flex-1">
                        <label className="text-xs text-stone-500">Qty to order</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={item.quantity_ordered}
                            onChange={e => setItems(prev =>
                              prev.map(i => i.ingredient_id === item.ingredient_id
                                ? { ...i, quantity_ordered: e.target.value }
                                : i
                              )
                            )}
                            placeholder="0"
                            className="h-7 w-20 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                          />
                          <span className="text-xs text-stone-500">{item.unit}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        <label className="text-xs text-stone-500">Unit price</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={item.unit_price}
                            onChange={e => setItems(prev =>
                              prev.map(i => i.ingredient_id === item.ingredient_id
                                ? { ...i, unit_price: e.target.value }
                                : i
                              )
                            )}
                            placeholder="0.000"
                            className="h-7 w-full pl-5 pr-2 rounded border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                          />
                        </div>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={item.notes}
                      onChange={e => setItems(prev =>
                        prev.map(i => i.ingredient_id === item.ingredient_id
                          ? { ...i, notes: e.target.value }
                          : i
                        )
                      )}
                      placeholder="Notes (optional)"
                      className="h-7 w-full rounded border border-stone-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                ))}

                {availableToAdd.length > 0 && (
                  <div className="flex gap-2 pt-1">
                    <select
                      value={addIngredientId}
                      onChange={e => setAddIngredientId(e.target.value)}
                      className="flex-1 h-8 rounded border border-stone-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">Add ingredient...</option>
                      {availableToAdd.map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddItem}
                      disabled={!addIngredientId}
                    >
                      <Plus size={13} />
                      Add item
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-stone-700">Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any notes for this order..."
                  className="w-full rounded border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-stone-200">
              <Button className="w-full" onClick={handleCreate} loading={createPO.isPending}>
                Create Draft PO
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

interface StockRowData {
  stock: InventoryPerishable | null
  ingredient: IngredientWithSuppliers
}

function StockView({
  ingredients,
  stockRecords,
  canEdit,
  onOrderIngredient,
}: {
  ingredients: IngredientWithSuppliers[]
  stockRecords: InventoryPerishable[]
  canEdit: boolean
  onOrderIngredient: (ingredient: IngredientWithSuppliers) => void
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const perishableIngredients = ingredients.filter(
    i => i.is_perishable && i.item_type === 'ingredient'
  )

  const stockMap = new Map<string, InventoryPerishable>(
    stockRecords.map(s => [s.ingredient_id, s])
  )

  const rows: StockRowData[] = perishableIngredients.map(ing => ({
    ingredient: ing,
    stock: stockMap.get(ing.id) ?? null,
  }))

  const needsOrdering = rows.filter(r => {
    if (!r.stock) return false
    const s = stockStatus(r.stock)
    return s === 'low' || s === 'order'
  }).length

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-stone-800">Stock Levels</h2>
        {needsOrdering > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
            {needsOrdering} {needsOrdering === 1 ? 'item needs' : 'items need'} ordering
          </span>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Ingredient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Unit</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">On Hand</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Par Level</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Reorder Point</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Expiry</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-stone-400">
                  No perishable ingredients found. Mark ingredients as perishable to track stock here.
                </td>
              </tr>
            ) : (
              rows.map(({ ingredient, stock }) => {
                const status = stock ? stockStatus(stock) : null
                const isExpanded = expandedId === ingredient.id
                const today = new Date().toISOString().slice(0, 10)
                const isExpired = stock?.expiry_date ? stock.expiry_date < today : false

                return (
                  <React.Fragment key={ingredient.id}>
                    <tr
                      className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-semibold text-stone-900">{ingredient.name}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-stone-100 text-stone-600 text-xs font-medium">
                          {ingredient.unit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {stock
                          ? <span className="font-medium text-stone-800">{stock.quantity}</span>
                          : <span className="text-stone-300 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-right text-stone-600">
                        {stock?.par_level ?? <span className="text-stone-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-600">
                        {stock?.reorder_point ?? <span className="text-stone-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {stock?.expiry_date
                          ? <span className={isExpired ? 'text-red-600 font-medium' : 'text-stone-600'}>
                              {stock.expiry_date}
                              {isExpired && <span className="ml-1 text-xs">(expired)</span>}
                            </span>
                          : <span className="text-stone-300 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        {status
                          ? <StatusBadge status={status} />
                          : <span className="text-stone-300 text-xs">No data</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        {canEdit && (
                          <div className="flex items-center gap-2 justify-end">
                            {status === 'low' || status === 'order' ? (
                              <button
                                type="button"
                                onClick={() => onOrderIngredient(ingredient)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors border border-red-200"
                              >
                                <ChevronRight size={11} />
                                Order
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => setExpandedId(isExpanded ? null : ingredient.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 transition-colors"
                            >
                              <Pencil size={11} />
                              Update Count
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && stock && (
                      <UpdateCountRow
                        stock={stock}
                        ingredientUnit={ingredient.unit}
                        onCancel={() => setExpandedId(null)}
                      />
                    )}
                    {isExpanded && !stock && (
                      <UpdateCountRow
                        stock={{
                          id: '',
                          restaurant_id: '',
                          ingredient_id: ingredient.id,
                          quantity: 0,
                          unit: ingredient.unit,
                          par_level: null,
                          reorder_point: null,
                          expiry_date: null,
                          location: null,
                          counted_at: null,
                          notes: null,
                        }}
                        ingredientUnit={ingredient.unit}
                        onCancel={() => setExpandedId(null)}
                      />
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PantryUpdateRow({
  stock,
  ingredientUnit,
  onCancel,
}: {
  stock: InventoryNonperishable
  ingredientUnit: string
  onCancel: () => void
}) {
  const upsert = useUpsertNonperishableStock()
  const [quantity, setQuantity] = useState(stock.quantity.toString())
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    const qty = parseFloat(quantity)
    if (isNaN(qty) || qty < 0) {
      setError('Quantity must be a valid number')
      return
    }
    setError(null)
    try {
      await upsert.mutateAsync({
        ingredient_id: stock.ingredient_id,
        quantity: qty,
        unit: ingredientUnit,
        par_level: stock.par_level,
        reorder_point: stock.reorder_point,
      })
      onCancel()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  return (
    <tr className="bg-amber-50 border-b border-amber-100">
      <td colSpan={5} className="px-4 py-3">
        <div className="flex flex-wrap gap-3 items-end">
          {error && <p className="w-full text-sm text-red-600">{error}</p>}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-600">On hand ({ingredientUnit})</label>
            <input
              type="number"
              min="0"
              step="0.001"
              autoFocus
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              className="h-8 w-28 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-2 pb-0.5">
            <Button size="sm" onClick={handleSave} loading={upsert.isPending}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </td>
    </tr>
  )
}

function PantryStockView({
  ingredients,
  stockRecords,
  canEdit,
}: {
  ingredients: IngredientWithSuppliers[]
  stockRecords: InventoryNonperishable[]
  canEdit: boolean
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const nonperishableIngredients = ingredients.filter(
    i => !i.is_perishable && i.item_type === 'ingredient'
  )

  const stockMap = new Map<string, InventoryNonperishable>(
    stockRecords.map(s => [s.ingredient_id, s])
  )

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-stone-800">Pantry Stock</h2>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Ingredient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Unit</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">On Hand</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Last Counted</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {nonperishableIngredients.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-stone-400">
                  No pantry items yet. Add a non-perishable ingredient to track its count here.
                </td>
              </tr>
            ) : (
              nonperishableIngredients.map(ingredient => {
                const stock = stockMap.get(ingredient.id) ?? null
                const isExpanded = expandedId === ingredient.id

                return (
                  <React.Fragment key={ingredient.id}>
                    <tr className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-stone-900">
                        {ingredient.name}
                        {ingredient.brand && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700 border border-brand-200">
                            {ingredient.brand}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-stone-100 text-stone-600 text-xs font-medium">
                          {ingredient.unit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {stock
                          ? <span className="font-medium text-stone-800">{stock.quantity}</span>
                          : <span className="text-stone-300 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-stone-600">
                        {stock?.counted_at ? formatDate(stock.counted_at) : <span className="text-stone-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {canEdit && (
                          <div className="flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => setExpandedId(isExpanded ? null : ingredient.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 transition-colors"
                            >
                              <Pencil size={11} />
                              Update Count
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <PantryUpdateRow
                        stock={stock ?? {
                          id: '',
                          restaurant_id: '',
                          ingredient_id: ingredient.id,
                          quantity: 0,
                          unit: ingredient.unit,
                          par_level: null,
                          reorder_point: null,
                          counted_at: null,
                        }}
                        ingredientUnit={ingredient.unit}
                        onCancel={() => setExpandedId(null)}
                      />
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EditPanel({
  ingredient,
  defaultType,
  onClose,
}: {
  ingredient: IngredientWithSuppliers | null
  defaultType: ItemType
  onClose: () => void
}) {
  const { data: suppliers } = useSuppliers()
  const saveIngredient = useSaveIngredient()
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  const [form, setForm] = useState<PanelState>({
    id: ingredient?.id,
    name: ingredient?.name ?? '',
    brand: ingredient?.brand ?? '',
    unit: ingredient?.unit ?? 'g',
    cost_per_unit: ingredient?.cost_per_unit?.toString() ?? '',
    is_perishable: ingredient?.is_perishable ?? false,
    item_type: ingredient?.item_type ?? defaultType,
    material_category: ingredient?.material_category ?? null,
    attachedSuppliers: ingredient?.ingredient_suppliers ?? [],
    addSupplierId: '',
  })
  const [supplierPrices, setSupplierPrices] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const s of ingredient?.ingredient_suppliers ?? []) {
      map[s.id] = s.unit_price?.toString() ?? ''
    }
    return map
  })
  const [error, setError] = useState<string | null>(null)
  const [savingSupplier, setSavingSupplier] = useState(false)

  useEffect(() => {
    if (ingredient) {
      setForm({
        id: ingredient.id,
        name: ingredient.name,
        brand: ingredient.brand ?? '',
        unit: ingredient.unit,
        cost_per_unit: ingredient.cost_per_unit?.toString() ?? '',
        is_perishable: ingredient.is_perishable,
        item_type: ingredient.item_type ?? 'ingredient',
        material_category: ingredient.material_category ?? null,
        attachedSuppliers: ingredient.ingredient_suppliers ?? [],
        addSupplierId: '',
      })
      const map: Record<string, string> = {}
      for (const s of ingredient.ingredient_suppliers ?? []) {
        map[s.id] = s.unit_price?.toString() ?? ''
      }
      setSupplierPrices(map)
    }
  }, [ingredient?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }
    setError(null)
    try {
      if (form.id) {
        for (const s of form.attachedSuppliers) {
          const price = supplierPrices[s.id]
          const parsed = price !== '' ? parseFloat(price) : null
          if (parsed !== s.unit_price) {
            await supabase
              .from('ingredient_suppliers')
              .update({ unit_price: parsed } as Record<string, unknown>)
              .eq('id', s.id)
          }
        }
      }

      await saveIngredient.mutateAsync({
        ...(form.id ? { id: form.id } : {}),
        name: form.name,
        brand: form.brand || null,
        unit: form.unit,
        cost_per_unit: form.cost_per_unit ? parseFloat(form.cost_per_unit) : null,
        is_perishable: form.item_type === 'ingredient' ? form.is_perishable : false,
        item_type: form.item_type,
        material_category: form.item_type === 'material' ? (form.material_category ?? 'other') : null,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  async function handleAddSupplier() {
    if (!form.addSupplierId || !form.id) return
    setSavingSupplier(true)
    try {
      const alreadyAttached = form.attachedSuppliers.some(s => s.supplier_id === form.addSupplierId)
      if (alreadyAttached) return

      const { data, error } = await supabase
        .from('ingredient_suppliers')
        .insert({
          ingredient_id: form.id,
          supplier_id: form.addSupplierId,
          is_preferred: form.attachedSuppliers.length === 0,
          unit_price: null,
          notes: null,
        } as Record<string, unknown>)
        .select('*, supplier:suppliers(*)')
        .single()

      if (error) throw error

      const newEntry = data as AttachedSupplier
      setForm(f => ({
        ...f,
        attachedSuppliers: [...f.attachedSuppliers, newEntry],
        addSupplierId: '',
      }))
      setSupplierPrices(p => ({ ...p, [newEntry.id]: '' }))
      qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
    } finally {
      setSavingSupplier(false)
    }
  }

  async function handleTogglePreferred(supplierId: string) {
    if (!form.id) return
    const updated = form.attachedSuppliers.map(s => ({
      ...s,
      is_preferred: s.supplier_id === supplierId,
    }))
    setForm(f => ({ ...f, attachedSuppliers: updated }))
    for (const s of updated) {
      await supabase
        .from('ingredient_suppliers')
        .update({ is_preferred: s.is_preferred } as Record<string, unknown>)
        .eq('id', s.id)
    }
    qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
  }

  async function handleRemoveSupplier(supplierLinkId: string) {
    setForm(f => ({ ...f, attachedSuppliers: f.attachedSuppliers.filter(s => s.id !== supplierLinkId) }))
    setSupplierPrices(p => { const n = { ...p }; delete n[supplierLinkId]; return n })
    await supabase.from('ingredient_suppliers').delete().eq('id', supplierLinkId)
    qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
  }

  async function handlePriceBlur(supplierLinkId: string) {
    const price = supplierPrices[supplierLinkId]
    const parsed = price !== '' ? parseFloat(price) : null
    await supabase
      .from('ingredient_suppliers')
      .update({ unit_price: parsed } as Record<string, unknown>)
      .eq('id', supplierLinkId)
    qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
  }

  const availableSuppliers = (suppliers ?? []).filter(
    s => !form.attachedSuppliers.some(a => a.supplier_id === s.id)
  )

  const isNew = !form.id
  const title = isNew
    ? (form.item_type === 'material' ? 'Add Material' : 'Add Ingredient')
    : (form.item_type === 'material' ? 'Edit Material' : 'Edit Ingredient')

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-semibold text-stone-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Type</label>
            <div className="flex gap-2">
              {([
                { value: 'ingredient' as const, label: 'Food Ingredient', icon: <Leaf size={13} /> },
                { value: 'material' as const, label: 'Material / Supply', icon: <Package size={13} /> },
              ]).map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, item_type: opt.value }))}
                  className={[
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded border text-sm font-medium transition-colors',
                    form.item_type === opt.value
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-brand-400',
                  ].join(' ')}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Name"
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />

          <Input
            label="Brand / Manufacturer"
            value={form.brand}
            onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
          />

          <div className="flex gap-3">
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-sm font-medium text-stone-700">Unit</label>
              <select
                value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {UNITS.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <Input
                label="Base cost / unit"
                type="number"
                min="0"
                step="0.001"
                prefix="$"
                value={form.cost_per_unit}
                onChange={e => setForm(f => ({ ...f, cost_per_unit: e.target.value }))}
              />
            </div>
          </div>

          {form.item_type === 'material' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-stone-700">Category</label>
              <select
                value={form.material_category ?? ''}
                onChange={e => setForm(f => ({ ...f, material_category: (e.target.value || null) as MaterialCategory | null }))}
                className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">Select category...</option>
                {MATERIAL_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          {form.item_type === 'ingredient' && (
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_perishable}
                onChange={e => setForm(f => ({ ...f, is_perishable: e.target.checked }))}
                className="rounded border-stone-300 text-brand-500 focus:ring-brand-500"
              />
              Perishable
            </label>
          )}

          <div className="space-y-3 pt-2 border-t border-stone-200">
            <p className="text-sm font-semibold text-stone-700">Suppliers &amp; Pricing</p>
            <p className="text-xs text-stone-400 -mt-2">Set a price per supplier — the preferred supplier's price is used in costing.</p>

            {form.attachedSuppliers.length === 0 && (
              <p className="text-xs text-stone-400">{isNew ? 'Save first, then attach suppliers.' : 'No suppliers attached'}</p>
            )}

            {form.attachedSuppliers.map(as => (
              <div key={as.id} className="p-2.5 rounded bg-stone-50 border border-stone-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-800">{as.supplier?.name ?? 'Unknown'}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleTogglePreferred(as.supplier_id)}
                      className={[
                        'transition-colors',
                        as.is_preferred ? 'text-warning' : 'text-stone-300 hover:text-stone-400',
                      ].join(' ')}
                      title={as.is_preferred ? 'Preferred supplier' : 'Set as preferred'}
                    >
                      <Star size={14} fill={as.is_preferred ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSupplier(as.id)}
                      className="text-stone-300 hover:text-danger transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-stone-500 w-24 shrink-0">Price / {form.unit}</span>
                  <div className="relative flex-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 text-xs">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={supplierPrices[as.id] ?? ''}
                      onChange={e => setSupplierPrices(p => ({ ...p, [as.id]: e.target.value }))}
                      onBlur={() => handlePriceBlur(as.id)}
                      placeholder="0.000"
                      className="w-full pl-6 pr-2 h-7 rounded border border-stone-300 bg-white text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
              </div>
            ))}

            {!isNew && availableSuppliers.length > 0 && (
              <div className="flex gap-2">
                <select
                  value={form.addSupplierId}
                  onChange={e => setForm(f => ({ ...f, addSupplierId: e.target.value }))}
                  className="flex-1 h-8 rounded border border-stone-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Add supplier...</option>
                  {availableSuppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddSupplier}
                  loading={savingSupplier}
                  disabled={!form.addSupplierId}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-stone-200">
          <Button className="w-full" onClick={handleSave} loading={saveIngredient.isPending}>
            {isNew ? (form.item_type === 'material' ? 'Save Material' : 'Save Ingredient') : 'Save Changes'}
          </Button>
        </div>
      </div>
    </>
  )
}

const MATERIAL_CATEGORY_LABELS: Record<MaterialCategory, string> = {
  packaging: 'Packaging',
  cleaning: 'Cleaning',
  kitchen_supply: 'Kitchen Supply',
  other: 'Other',
}

export function IngredientsPage() {
  const { data: ingredients, isLoading } = useIngredients()
  const { data: stockRecords } = useInventoryPerishable()
  const { data: nonperishableStock } = useInventoryNonperishable()
  const role = useAuthStore(s => s.role)

  const canAccessStock = role != null && STOCK_ROLES.includes(role)

  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [editingIngredient, setEditingIngredient] = useState<
    IngredientWithSuppliers | null | 'new'
  >(null)
  const [quickPOIngredients, setQuickPOIngredients] = useState<IngredientWithSuppliers[]>([])
  const [showQuickPO, setShowQuickPO] = useState(false)

  if (isLoading) return <PageSpinner />

  const all = ingredients ?? []

  const foodItems = all.filter(i => i.item_type === 'ingredient')
  const materialItems = all.filter(i => i.item_type === 'material')

  const tabItems = activeTab === 'ingredient' ? foodItems
    : activeTab === 'material' ? materialItems
    : all

  const filtered = tabItems.filter(i => {
    const q = search.toLowerCase()
    return (
      i.name.toLowerCase().includes(q) ||
      (i.brand ?? '').toLowerCase().includes(q)
    )
  })

  const missingBrand = foodItems.filter(i => !i.brand).length
  const missingCost  = all.filter(i => getEffectiveCost(i) == null).length

  const baseTabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: all.length },
    { key: 'ingredient', label: 'Food Ingredients', count: foodItems.length },
    { key: 'material', label: 'Materials & Supplies', count: materialItems.length },
  ]

  const tabs = canAccessStock
    ? [...baseTabs, { key: 'stock' as TabKey, label: 'Stock', count: 0 }]
    : baseTabs

  const showMaterialCols = activeTab === 'material'
  const showIngredientCols = activeTab === 'ingredient'

  function handleOrderIngredient(ingredient: IngredientWithSuppliers) {
    setQuickPOIngredients([ingredient])
    setShowQuickPO(true)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Inventory</h1>
          <p className="text-sm text-stone-500 mt-0.5">Food ingredients and kitchen materials with suppliers and costs</p>
        </div>
        {activeTab !== 'stock' && (
          <Button
            size="sm"
            onClick={() => setEditingIngredient('new')}
          >
            + Add {activeTab === 'material' ? 'Material' : 'Ingredient'}
          </Button>
        )}
      </div>

      {all.length > 0 && activeTab !== 'stock' && (
        <div className="flex gap-4 flex-wrap">
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{foodItems.length}</span> food ingredients
          </span>
          <span className="text-sm text-stone-400">·</span>
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{materialItems.length}</span> materials
          </span>
          {missingBrand > 0 && (
            <>
              <span className="text-sm text-stone-400">·</span>
              <span className="text-sm text-warning-dark">
                <span className="font-semibold">{missingBrand}</span> food items missing brand
              </span>
            </>
          )}
          {missingCost > 0 && (
            <>
              <span className="text-sm text-stone-400">·</span>
              <span className="text-sm text-danger">
                <span className="font-semibold">{missingCost}</span> missing cost
              </span>
            </>
          )}
        </div>
      )}

      <div className="flex gap-1 border-b border-stone-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-stone-500 hover:text-stone-700',
            ].join(' ')}
          >
            {tab.label}
            {tab.key !== 'stock' && tab.count > 0 && (
              <span className={[
                'ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs',
                activeTab === tab.key
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-stone-100 text-stone-500',
              ].join(' ')}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'stock' ? (
        <>
          <StockView
            ingredients={all}
            stockRecords={stockRecords ?? []}
            canEdit={canAccessStock}
            onOrderIngredient={handleOrderIngredient}
          />
          <PantryStockView
            ingredients={all}
            stockRecords={nonperishableStock ?? []}
            canEdit={canAccessStock}
          />
        </>
      ) : (
        <>
          <div className="max-w-sm -mt-2">
            <Input
              placeholder="Search by name or brand..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Brand</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Unit</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Cost / Unit</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Preferred Supplier</th>
                  {showMaterialCols && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Category</th>
                  )}
                  {showIngredientCols && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Perishable</th>
                  )}
                  {activeTab === 'all' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Type</th>
                  )}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-stone-400">
                      {search
                        ? 'No items match your search'
                        : activeTab === 'material'
                          ? 'No materials yet. Add packaging, cleaning supplies, and more.'
                          : activeTab === 'ingredient'
                            ? 'No food ingredients yet.'
                            : 'No items yet. Add your first one.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map(ing => {
                    const effectiveCost = getEffectiveCost(ing)
                    const preferredSupplier = ing.ingredient_suppliers.find(s => s.is_preferred)
                    const hasSupplierPrice = preferredSupplier?.unit_price != null

                    return (
                      <tr key={ing.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-semibold text-stone-900">{ing.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          {ing.brand
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700 border border-brand-200">{ing.brand}</span>
                            : ing.item_type === 'ingredient'
                              ? <button
                                  type="button"
                                  onClick={() => setEditingIngredient(ing)}
                                  className="text-xs text-warning-dark hover:underline"
                                >Set brand</button>
                              : <span className="text-stone-300 text-xs">—</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-stone-100 text-stone-600 text-xs font-medium">{ing.unit}</span>
                        </td>
                        <td className="px-4 py-3">
                          {effectiveCost != null
                            ? <span className="font-medium text-stone-800">
                                ${effectiveCost.toFixed(3)}
                                <span className="text-stone-400 font-normal">/{ing.unit}</span>
                                {hasSupplierPrice && <span className="ml-1 text-xs text-stone-400">(supplier)</span>}
                              </span>
                            : <button
                                type="button"
                                onClick={() => setEditingIngredient(ing)}
                                className="text-xs text-danger hover:underline"
                              >Set cost</button>
                          }
                        </td>
                        <td className="px-4 py-3 text-stone-600 text-sm">{getPreferredSupplierName(ing)}</td>
                        {showMaterialCols && (
                          <td className="px-4 py-3">
                            {ing.material_category
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-stone-100 text-stone-600">
                                  {MATERIAL_CATEGORY_LABELS[ing.material_category]}
                                </span>
                              : <span className="text-stone-300 text-xs">—</span>
                            }
                          </td>
                        )}
                        {showIngredientCols && (
                          <td className="px-4 py-3">
                            {ing.is_perishable
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-light text-warning-dark">Perishable</span>
                              : <span className="text-stone-300 text-xs">—</span>
                            }
                          </td>
                        )}
                        {activeTab === 'all' && (
                          <td className="px-4 py-3">
                            {ing.item_type === 'material'
                              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-stone-100 text-stone-600">
                                  <Package size={10} /> Material
                                </span>
                              : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-success-light text-success-dark">
                                  <Leaf size={10} /> Ingredient
                                </span>
                            }
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setEditingIngredient(ing)}
                            className="text-stone-400 hover:text-stone-700 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editingIngredient !== null && (
        <EditPanel
          ingredient={editingIngredient === 'new' ? null : editingIngredient}
          defaultType={activeTab === 'material' ? 'material' : 'ingredient'}
          onClose={() => setEditingIngredient(null)}
        />
      )}

      {showQuickPO && (
        <QuickPOPanel
          initialIngredients={quickPOIngredients}
          allIngredients={all}
          onClose={() => {
            setShowQuickPO(false)
            setQuickPOIngredients([])
          }}
        />
      )}
    </div>
  )
}
